/**
 * Textos del juego en los cuatro idiomas del spec: es, en, pt, ru.
 *
 * El espanol es el idioma por defecto: el publico objetivo es LatAm y el
 * `language_code` de Telegram manda solo si tiene traduccion.
 *
 * Los textos llevan tildes y enes de verdad (regla de la casa: lo que lee una
 * persona va bien escrito). El fichero es UTF-8 y vite lo mete en el bundle tal
 * cual, asi que no hay riesgo de que se pierdan en el camino.
 */

export type Idioma = 'es' | 'en' | 'pt' | 'ru';

/** Las claves de todos los textos. TypeScript obliga a que los cuatro idiomas las tengan todas. */
export interface Textos {
  // Pantalla de inicio
  jugar: string;
  record: string;
  monedas: string;
  duelos: string;
  clan: string;
  ranking: string;
  pozo: string;
  tareas: string;
  referidos: string;
  tienda: string;
  perfil: string;

  // Durante la partida
  metros: string;
  combo: string;

  // Game over
  finPartida: string;
  distancia: string;
  monedasGanadas: string;
  nuevoRecord: string;
  casiLoLogras: string;
  casiDetalle: (faltaban: number) => string;
  otraVez: string;
  revivirConAnuncio: string;
  compartirRecord: string;
  volverInicio: string;

  // Anuncios recompensados
  revivir: string;
  revivirRestantes: (n: number) => string;
  noRevivir: string;
  sinMasRevivires: string;
  jugarConX2: string;
  x2Listo: string;
  x2Activo: string;
  cajaDiaria: (monedas: number) => string;
  cajaDiariaUsada: string;
  cajaAbierta: (monedas: number) => string;
  cargandoAnuncio: string;
  anuncioDePrueba: string;
  anuncioPruebaPie: string;
  anuncioNoCompletado: string;
  anuncioSinConfirmar: string;
  anuncioNoDisponible: string;

  // Duelos
  retar: string;
  aceptarReto: string;
  teReta: (quien: string, metros: number) => string;
  supera: (metros: number) => string;
  duelosGanados: string;
  rachaVictorias: string;

  // Avisos
  cargando: string;
  sinConexion: string;
  errorGenerico: string;
  pausado: string;
  continuar: string;

  // Legal (texto fijo que exige el spec)
  legalPuntosTemporada: string;

  // Biomas
  biomaCiudad: string;
  biomaEstratosfera: string;
  biomaEspacio: string;

  // Creador de avatares
  //
  // Los colores NO llevan nombre a proposito: un circulo naranja se entiende
  // en los cuatro idiomas y en ninguno hace falta leer "naranja". Solo tienen
  // texto las cosas que no se ven de un vistazo.
  miPersonaje: string;
  avatarCuerpo: string;
  avatarChico: string;
  avatarChica: string;
  avatarPeinado: string;
  avatarPelo: string;
  avatarPiel: string;
  avatarTraje: string;
  avatarGuardar: string;
  avatarGuardado: string;
  avatarError: string;
  peinadoRapado: string;
  peinadoCorto: string;
  peinadoRizado: string;
  peinadoColeta: string;
  peinadoMelena: string;
  peinadoMono: string;
  trajeBloqueadoInvitado: string;
  trajeBloqueadoReclutador: string;
  trajeBloqueadoPase: string;
}

const es: Textos = {
  jugar: 'Jugar',
  record: 'Récord',
  monedas: 'Monedas',
  duelos: 'Duelos',
  clan: 'Clan',
  ranking: 'Ranking',
  pozo: 'Pozo',
  tareas: 'Tareas',
  referidos: 'Invitar',
  tienda: 'Tienda',
  perfil: 'Perfil',

  metros: 'm',
  combo: 'combo',

  finPartida: 'Fin de la partida',
  distancia: 'Distancia',
  monedasGanadas: 'Monedas',
  nuevoRecord: '¡Nuevo récord!',
  casiLoLogras: '¡Casi lo logras!',
  casiDetalle: (faltaban) => `Te faltaban ${faltaban} m para tu récord`,
  otraVez: 'Otra vez',
  revivirConAnuncio: 'Revivir (ver anuncio)',
  compartirRecord: 'Compartir récord',
  volverInicio: 'Inicio',

  revivir: 'Seguir jugando',
  revivirRestantes: (n) => (n === 1 ? 'Te queda 1' : `Te quedan ${n}`),
  noRevivir: 'Terminar la partida',
  sinMasRevivires: 'Ya has revivido 3 veces en esta partida',
  jugarConX2: 'Jugar con monedas ×2',
  x2Listo: '×2 listo para tu próxima partida',
  x2Activo: 'Monedas ×2 activas',
  cajaDiaria: (monedas) => `Caja diaria: ${monedas} monedas`,
  cajaDiariaUsada: 'Ya has abierto la caja de hoy. Vuelve mañana.',
  cajaAbierta: (monedas) => `¡+${monedas} monedas!`,
  cargandoAnuncio: 'Cargando el anuncio…',
  anuncioDePrueba: 'Anuncio de prueba',
  anuncioPruebaPie:
    'Esto es una simulación para poder probar el juego. No es un anuncio real y no genera ningún ingreso.',
  anuncioNoCompletado: 'No has visto el anuncio completo, así que no hay recompensa.',
  anuncioSinConfirmar:
    'No he podido confirmar el anuncio. Si lo viste entero, inténtalo otra vez.',
  anuncioNoDisponible: 'Ahora mismo no hay anuncios disponibles.',

  retar: 'Retar a un amigo',
  aceptarReto: 'Aceptar reto',
  teReta: (quien, metros) => `${quien} te reta: supera ${metros} m 🚀`,
  supera: (metros) => `Supera ${metros} m`,
  duelosGanados: 'Duelos ganados',
  rachaVictorias: 'Racha',

  cargando: 'Cargando…',
  sinConexion: 'Sin conexión. Revisa tus datos y vuelve a intentarlo.',
  errorGenerico: 'Algo salió mal. Inténtalo de nuevo.',
  pausado: 'Pausa',
  continuar: 'Continuar',

  legalPuntosTemporada:
    'Los puntos de temporada no tienen valor monetario y podrían tener utilidad futura dentro del ecosistema.',

  biomaCiudad: 'Ciudad',
  biomaEstratosfera: 'Estratosfera',
  biomaEspacio: 'Espacio',

  miPersonaje: 'Mi personaje',
  avatarCuerpo: 'Cuerpo',
  avatarChico: 'Chico',
  avatarChica: 'Chica',
  avatarPeinado: 'Peinado',
  avatarPelo: 'Color de pelo',
  avatarPiel: 'Tono de piel',
  avatarTraje: 'Ropa',
  avatarGuardar: 'Guardar',
  avatarGuardado: '¡Guardado!',
  avatarError: 'No se pudo guardar. Inténtalo otra vez.',
  peinadoRapado: 'Rapado',
  peinadoCorto: 'Corto',
  peinadoRizado: 'Rizado',
  peinadoColeta: 'Coleta',
  peinadoMelena: 'Melena',
  peinadoMono: 'Moño',
  trajeBloqueadoInvitado: 'Se desbloquea al entrar con la invitación de un amigo',
  trajeBloqueadoReclutador: 'Se desbloquea cuando 3 de tus invitados se activen',
  trajeBloqueadoPase: 'Es del Pase de Temporada, que todavía no está a la venta',
};

const en: Textos = {
  jugar: 'Play',
  record: 'Best',
  monedas: 'Coins',
  duelos: 'Duels',
  clan: 'Clan',
  ranking: 'Leaderboard',
  pozo: 'Prize pool',
  tareas: 'Tasks',
  referidos: 'Invite',
  tienda: 'Shop',
  perfil: 'Profile',

  metros: 'm',
  combo: 'combo',

  finPartida: 'Game over',
  distancia: 'Distance',
  monedasGanadas: 'Coins',
  nuevoRecord: 'New record!',
  casiLoLogras: 'So close!',
  casiDetalle: (faltaban) => `You were ${faltaban} m from your best`,
  otraVez: 'Again',
  revivirConAnuncio: 'Revive (watch ad)',

  revivir: 'Keep playing',
  revivirRestantes: (n) => (n === 1 ? '1 left' : `${n} left`),
  noRevivir: 'End the run',
  sinMasRevivires: "You've already revived 3 times in this run",
  jugarConX2: 'Play with ×2 coins',
  x2Listo: '×2 ready for your next run',
  x2Activo: '×2 coins active',
  cajaDiaria: (monedas) => `Daily box: ${monedas} coins`,
  cajaDiariaUsada: "You've already opened today's box. Come back tomorrow.",
  cajaAbierta: (monedas) => `+${monedas} coins!`,
  cargandoAnuncio: 'Loading the ad…',
  anuncioDePrueba: 'Test ad',
  anuncioPruebaPie:
    'This is a simulation so the game can be tested. It is not a real ad and it earns nothing.',
  anuncioNoCompletado: "You didn't watch the whole ad, so there's no reward.",
  anuncioSinConfirmar:
    "I couldn't confirm the ad. If you watched it all, please try again.",
  anuncioNoDisponible: 'No ads available right now.',
  compartirRecord: 'Share record',
  volverInicio: 'Home',

  retar: 'Challenge a friend',
  aceptarReto: 'Accept challenge',
  teReta: (quien, metros) => `${quien} challenges you: beat ${metros} m 🚀`,
  supera: (metros) => `Beat ${metros} m`,
  duelosGanados: 'Duels won',
  rachaVictorias: 'Streak',

  cargando: 'Loading…',
  sinConexion: 'No connection. Check your data and try again.',
  errorGenerico: 'Something went wrong. Try again.',
  pausado: 'Paused',
  continuar: 'Continue',

  legalPuntosTemporada:
    'Season points have no monetary value and may have future utility within the ecosystem.',

  biomaCiudad: 'City',
  biomaEstratosfera: 'Stratosphere',
  biomaEspacio: 'Space',

  miPersonaje: 'My character',
  avatarCuerpo: 'Body',
  avatarChico: 'Boy',
  avatarChica: 'Girl',
  avatarPeinado: 'Hair',
  avatarPelo: 'Hair colour',
  avatarPiel: 'Skin tone',
  avatarTraje: 'Outfit',
  avatarGuardar: 'Save',
  avatarGuardado: 'Saved!',
  avatarError: "Couldn't save it. Try again.",
  peinadoRapado: 'Buzz cut',
  peinadoCorto: 'Short',
  peinadoRizado: 'Curly',
  peinadoColeta: 'Ponytail',
  peinadoMelena: 'Long',
  peinadoMono: 'Bun',
  trajeBloqueadoInvitado: "Unlocked when you join through a friend's invite",
  trajeBloqueadoReclutador: 'Unlocked when 3 of your invites get activated',
  trajeBloqueadoPase: "It's a Season Pass outfit, not on sale yet",
};

const pt: Textos = {
  jugar: 'Jogar',
  record: 'Recorde',
  monedas: 'Moedas',
  duelos: 'Duelos',
  clan: 'Clã',
  ranking: 'Ranking',
  pozo: 'Prêmio',
  tareas: 'Tarefas',
  referidos: 'Convidar',
  tienda: 'Loja',
  perfil: 'Perfil',

  metros: 'm',
  combo: 'combo',

  finPartida: 'Fim de jogo',
  distancia: 'Distância',
  monedasGanadas: 'Moedas',
  nuevoRecord: 'Novo recorde!',
  casiLoLogras: 'Quase!',
  casiDetalle: (faltaban) => `Faltaram ${faltaban} m para o seu recorde`,
  otraVez: 'De novo',
  revivirConAnuncio: 'Reviver (ver anúncio)',

  revivir: 'Continuar jogando',
  revivirRestantes: (n) => (n === 1 ? 'Resta 1' : `Restam ${n}`),
  noRevivir: 'Terminar a partida',
  sinMasRevivires: 'Você já reviveu 3 vezes nesta partida',
  jugarConX2: 'Jogar com moedas ×2',
  x2Listo: '×2 pronto para a próxima partida',
  x2Activo: 'Moedas ×2 ativas',
  cajaDiaria: (monedas) => `Caixa diária: ${monedas} moedas`,
  cajaDiariaUsada: 'Você já abriu a caixa de hoje. Volte amanhã.',
  cajaAbierta: (monedas) => `+${monedas} moedas!`,
  cargandoAnuncio: 'Carregando o anúncio…',
  anuncioDePrueba: 'Anúncio de teste',
  anuncioPruebaPie:
    'Isto é uma simulação para poder testar o jogo. Não é um anúncio real e não gera receita.',
  anuncioNoCompletado: 'Você não viu o anúncio completo, então não há recompensa.',
  anuncioSinConfirmar:
    'Não consegui confirmar o anúncio. Se você viu até o fim, tente de novo.',
  anuncioNoDisponible: 'Não há anúncios disponíveis agora.',
  compartirRecord: 'Compartilhar recorde',
  volverInicio: 'Início',

  retar: 'Desafiar um amigo',
  aceptarReto: 'Aceitar desafio',
  teReta: (quien, metros) => `${quien} te desafia: supere ${metros} m 🚀`,
  supera: (metros) => `Supere ${metros} m`,
  duelosGanados: 'Duelos ganhos',
  rachaVictorias: 'Sequência',

  cargando: 'Carregando…',
  sinConexion: 'Sem conexão. Verifique seus dados e tente novamente.',
  errorGenerico: 'Algo deu errado. Tente de novo.',
  pausado: 'Pausa',
  continuar: 'Continuar',

  legalPuntosTemporada:
    'Os pontos de temporada não têm valor monetário e poderão ter utilidade futura dentro do ecossistema.',

  biomaCiudad: 'Cidade',
  biomaEstratosfera: 'Estratosfera',
  biomaEspacio: 'Espaço',

  miPersonaje: 'Meu personagem',
  avatarCuerpo: 'Corpo',
  avatarChico: 'Menino',
  avatarChica: 'Menina',
  avatarPeinado: 'Cabelo',
  avatarPelo: 'Cor do cabelo',
  avatarPiel: 'Tom de pele',
  avatarTraje: 'Roupa',
  avatarGuardar: 'Salvar',
  avatarGuardado: 'Salvo!',
  avatarError: 'Não deu para salvar. Tente de novo.',
  peinadoRapado: 'Raspado',
  peinadoCorto: 'Curto',
  peinadoRizado: 'Cacheado',
  peinadoColeta: 'Rabo de cavalo',
  peinadoMelena: 'Comprido',
  peinadoMono: 'Coque',
  trajeBloqueadoInvitado: 'Libera quando você entrar pelo convite de um amigo',
  trajeBloqueadoReclutador: 'Libera quando 3 dos seus convidados forem ativados',
  trajeBloqueadoPase: 'É do Passe de Temporada, que ainda não está à venda',
};

const ru: Textos = {
  jugar: 'Играть',
  record: 'Рекорд',
  monedas: 'Монеты',
  duelos: 'Дуэли',
  clan: 'Клан',
  ranking: 'Рейтинг',
  pozo: 'Призовой фонд',
  tareas: 'Задания',
  referidos: 'Пригласить',
  tienda: 'Магазин',
  perfil: 'Профиль',

  metros: 'м',
  combo: 'комбо',

  finPartida: 'Игра окончена',
  distancia: 'Дистанция',
  monedasGanadas: 'Монеты',
  nuevoRecord: 'Новый рекорд!',
  casiLoLogras: 'Почти!',
  casiDetalle: (faltaban) => `До рекорда не хватило ${faltaban} м`,
  otraVez: 'Ещё раз',
  revivirConAnuncio: 'Возродиться (реклама)',

  revivir: 'Продолжить игру',
  revivirRestantes: (n) => (n === 1 ? 'Осталась 1' : `Осталось ${n}`),
  noRevivir: 'Закончить забег',
  sinMasRevivires: 'Вы уже возрождались 3 раза в этом забеге',
  jugarConX2: 'Играть с ×2 монетами',
  x2Listo: '×2 готово для следующего забега',
  x2Activo: '×2 монеты активны',
  cajaDiaria: (monedas) => `Ежедневный ящик: ${monedas} монет`,
  cajaDiariaUsada: 'Вы уже открыли сегодняшний ящик. Возвращайтесь завтра.',
  cajaAbierta: (monedas) => `+${monedas} монет!`,
  cargandoAnuncio: 'Загрузка рекламы…',
  anuncioDePrueba: 'Тестовая реклама',
  anuncioPruebaPie:
    'Это симуляция для проверки игры. Это не настоящая реклама и она не приносит дохода.',
  anuncioNoCompletado: 'Вы не посмотрели рекламу до конца, награды нет.',
  anuncioSinConfirmar:
    'Не удалось подтвердить просмотр. Если вы смотрели до конца, попробуйте снова.',
  anuncioNoDisponible: 'Сейчас реклама недоступна.',
  compartirRecord: 'Поделиться рекордом',
  volverInicio: 'В начало',

  retar: 'Вызвать друга',
  aceptarReto: 'Принять вызов',
  teReta: (quien, metros) => `${quien} вызывает вас: побейте ${metros} м 🚀`,
  supera: (metros) => `Побейте ${metros} м`,
  duelosGanados: 'Побед в дуэлях',
  rachaVictorias: 'Серия',

  cargando: 'Загрузка…',
  sinConexion: 'Нет соединения. Проверьте интернет и попробуйте снова.',
  errorGenerico: 'Что-то пошло не так. Попробуйте снова.',
  pausado: 'Пауза',
  continuar: 'Продолжить',

  legalPuntosTemporada:
    'Очки сезона не имеют денежной стоимости и могут получить применение в экосистеме в будущем.',

  biomaCiudad: 'Город',
  biomaEstratosfera: 'Стратосфера',
  biomaEspacio: 'Космос',

  miPersonaje: 'Мой персонаж',
  avatarCuerpo: 'Фигура',
  avatarChico: 'Парень',
  avatarChica: 'Девушка',
  avatarPeinado: 'Причёска',
  avatarPelo: 'Цвет волос',
  avatarPiel: 'Тон кожи',
  avatarTraje: 'Одежда',
  avatarGuardar: 'Сохранить',
  avatarGuardado: 'Сохранено!',
  avatarError: 'Не удалось сохранить. Попробуй ещё раз.',
  peinadoRapado: 'Ёжик',
  peinadoCorto: 'Короткие',
  peinadoRizado: 'Кудрявые',
  peinadoColeta: 'Хвост',
  peinadoMelena: 'Длинные',
  peinadoMono: 'Пучок',
  trajeBloqueadoInvitado: 'Откроется, если зайти по приглашению друга',
  trajeBloqueadoReclutador: 'Откроется, когда активируются 3 твоих приглашённых',
  trajeBloqueadoPase: 'Это одежда Сезонного пропуска, он ещё не продаётся',
};

const IDIOMAS: Record<Idioma, Textos> = { es, en, pt, ru };

let actual: Textos = es;
let codigoActual: Idioma = 'es';

/**
 * Fija el idioma a partir del `language_code` de Telegram.
 * Si el idioma no esta traducido, se queda el espanol: es el publico principal
 * y un texto en espanol se entiende mejor que una clave sin traducir.
 */
export function fijarIdioma(codigo: string | undefined): Idioma {
  const corto = (codigo ?? 'es').slice(0, 2).toLowerCase() as Idioma;
  if (corto in IDIOMAS) {
    codigoActual = corto;
    actual = IDIOMAS[corto];
  } else {
    codigoActual = 'es';
    actual = es;
  }
  document.documentElement.lang = codigoActual;
  return codigoActual;
}

/** Los textos del idioma activo. */
export function t(): Textos {
  return actual;
}

export function idiomaActual(): Idioma {
  return codigoActual;
}

/** Formatea un numero con separador de miles segun el idioma. */
export function numero(n: number): string {
  const locales: Record<Idioma, string> = {
    es: 'es-ES',
    en: 'en-US',
    pt: 'pt-BR',
    ru: 'ru-RU',
  };
  return n.toLocaleString(locales[codigoActual]);
}

/**
 * Textos del flujo de duelos. Van aparte del objeto `Textos` principal para no
 * obligar a traducir los cuatro idiomas cada vez que se añade una pantalla:
 * aquí el español es el que manda y el resto cae en él si falta.
 */
const DUELOS: Record<Idioma, Record<string, string>> = {
  es: {
    retar: '⚔️ Retar a un amigo',
    creandoReto: 'Preparando el reto…',
    juegaTuReto: 'Juega tu marca y luego compártela',
    compartirReto: '📤 Compartir el reto',
    retoListo: '¡Reto listo!',
    retoListoDetalle: 'Compártelo y que intenten superarte',
    sinSaldo: 'No tienes monedas suficientes para apostar',
    duelosApagados: 'Los duelos no están disponibles ahora mismo',
    doblarPremio: '×2 al premio',
  },
  en: {
    retar: '⚔️ Challenge a friend',
    creandoReto: 'Setting up the challenge…',
    juegaTuReto: 'Play your run, then share it',
    compartirReto: '📤 Share the challenge',
    retoListo: 'Challenge ready!',
    retoListoDetalle: 'Share it and see if they can beat you',
    sinSaldo: 'Not enough coins to bet',
    duelosApagados: 'Duels are not available right now',
    doblarPremio: '×2 the prize',
  },
  pt: {
    retar: '⚔️ Desafiar um amigo',
    creandoReto: 'Preparando o desafio…',
    juegaTuReto: 'Jogue sua partida e depois compartilhe',
    compartirReto: '📤 Compartilhar o desafio',
    retoListo: 'Desafio pronto!',
    retoListoDetalle: 'Compartilhe e veja se superam você',
    sinSaldo: 'Moedas insuficientes para apostar',
    duelosApagados: 'Os duelos não estão disponíveis agora',
    doblarPremio: '×2 no prêmio',
  },
  ru: {
    retar: '⚔️ Вызвать друга',
    creandoReto: 'Готовим вызов…',
    juegaTuReto: 'Сыграйте, а потом поделитесь',
    compartirReto: '📤 Поделиться вызовом',
    retoListo: 'Вызов готов!',
    retoListoDetalle: 'Отправьте и посмотрите, смогут ли побить',
    sinSaldo: 'Недостаточно монет для ставки',
    duelosApagados: 'Дуэли сейчас недоступны',
    doblarPremio: '×2 к призу',
  },
};

/** Un texto del flujo de duelos. Cae al español si el idioma no lo tiene. */
export function td(clave: string): string {
  return DUELOS[codigoActual]?.[clave] ?? DUELOS.es[clave] ?? clave;
}
